// Renderer 100 % DÉDIÉ de la Mine premium (refonte 21/07). Aucune dépendance à render.ts
// (contrat multi-terminaux docs/refonte-hd/CONTRAT.md) : le héros vient du module procédural
// drawBaroudeur (net à toute taille), TOUT le reste est peint ici. Principe : les grandes pièces
// (3 couches de caverne en parallaxe, bande de sol) sont peintes UNE fois dans des canvas
// hors-écran en résolution écran finale, puis blitées par frame ; seuls les éléments vivants
// (halos de lanternes, étincelles, héros, invite) se dessinent chaque frame. Espace MONDE =
// 48 px/case, zoom appliqué par ctx.scale -> le vectoriel (héros, sol) sort net en 144 px/case.
// Purement cosmétique : jamais dans la simulation, le hash ou le réseau.
import { drawBaroudeur, type PoseName } from './baroudeur'
import { dashFxHideBody, dashFxTrack, drawDashFxBack, drawDashFxFront } from './dash-fx'
import { dashVariant } from './dash-variant'
import { currentEmote, drawEmoteBubble } from './emote-ui'
import { equipped } from './loadout'
import { drawParticles } from './particles'
import { petDessinable, resetPetHistory, updateAndDrawPet } from './pet'
import { PET_SPRITES } from './sprites'
import { avatarHue } from './prefs'
import { DEPTH_MULT, depthLabel, depthPromise } from '../shared/economy'
import { hueIndex } from '../shared/skins'
import { GameState } from '../shared/state'
import { isSolid } from '../shared/physics'
import { SUB, T } from '../shared/types'
import { regrowStage } from '../shared/dig'
import { rarityRays } from './mine-crack'
import { FLOOR_ROW, minePose } from './mine-scene'
import {
  crackSegments, implosionP, megaBlaze, MINE_CAM_Y, MINE_VIEW_W, MINE_ZOOM, mulberry32,
  palierWave, pickHitFlash, soilFeatures, SUSPENSE_PALIERS, suspenseGlow,
  type RainCoin, type SoilFeature, type SuspenseClock,
} from './mine-suspense'

export interface MineCam { x: number; y: number; zoom: number }

const W = 1344, H = 816 // canvas écran
const TSW = 48 // px monde par case
const PXW = TSW / SUB // px monde par sous-unité (6)
const COLS = 28, LAST_COL = COLS - 1
const SCROLL = (COLS * TSW - MINE_VIEW_W) * MINE_ZOOM // course écran totale de la caméra (2688)
const SEED = 20260721 // mêmes décors à chaque visite

/** '#rrggbb' + alpha -> 'rgba(...)' (stops de dégradés). */
function hexA(hex: string, a: number): string {
  const h = hex.replace('#', '')
  const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16)
  return `rgba(${r},${g},${b},${Math.max(0, Math.min(1, a))})`
}

function mkLayer(w: number): { cv: HTMLCanvasElement; g: CanvasRenderingContext2D } {
  const cv = document.createElement('canvas')
  cv.width = Math.ceil(w)
  cv.height = H
  return { cv, g: cv.getContext('2d')! }
}

// --- Couche LOINTAINE (facteur 0.2) : l'immensité de la caverne -------------------------------
// Dégradé profond, voûtes sombres, cristaux géants émissifs flous dans la brume.
function paintFar(g: CanvasRenderingContext2D, w: number): void {
  const sky = g.createLinearGradient(0, 0, 0, H)
  sky.addColorStop(0, '#07050e')
  sky.addColorStop(0.55, '#120b1e')
  sky.addColorStop(1, '#1a1030')
  g.fillStyle = sky
  g.fillRect(0, 0, w, H)
  const rand = mulberry32(SEED)
  // voûtes : grandes ellipses sombres qui mangent le haut (profondeur de plafond)
  g.fillStyle = '#0d0918'
  for (let i = 0; i < 4; i++) {
    const cx = (i + 0.5) * (w / 4) + (rand() - 0.5) * 120
    const ry = 90 + rand() * 70
    g.beginPath()
    g.ellipse(cx, -20, 190 + rand() * 120, ry, 0, 0, Math.PI * 2)
    g.fill()
  }
  // cristaux géants émissifs : ancrés BAS (ligne d'horizon du sol ≈ 543), facettés (face sombre +
  // facette claire), sous un halo radial de leur teinte — la brume basse passe ensuite par-dessus.
  const tints = ['#7cd7ff', '#b98bff', '#5db4ff', '#7cd7ff', '#b98bff']
  for (let i = 0; i < 5; i++) {
    const cx = rand() * w
    const baseY = H * (0.62 + rand() * 0.14)
    const hgt = 90 + rand() * 130
    const half = 0.24 * hgt
    const tint = tints[i]!
    const halo = g.createRadialGradient(cx, baseY - hgt * 0.5, 0, cx, baseY - hgt * 0.5, hgt * 2.2)
    halo.addColorStop(0, hexA(tint, 0.1))
    halo.addColorStop(1, hexA(tint, 0))
    g.fillStyle = halo
    g.fillRect(cx - hgt * 2.2, baseY - hgt * 0.5 - hgt * 2.2, hgt * 4.4, hgt * 4.4)
    g.fillStyle = hexA(tint, 0.14 + rand() * 0.08) // face droite (sombre)
    g.beginPath()
    g.moveTo(cx, baseY - hgt)
    g.lineTo(cx + half, baseY)
    g.lineTo(cx - half * 0.2, baseY)
    g.closePath()
    g.fill()
    g.fillStyle = hexA(tint, 0.3 + rand() * 0.12) // facette gauche (claire : le cristal « prend » la lumière)
    g.beginPath()
    g.moveTo(cx, baseY - hgt)
    g.lineTo(cx - half * 0.2, baseY)
    g.lineTo(cx - half, baseY)
    g.closePath()
    g.fill()
    g.fillStyle = hexA('#ffffff', 0.35) // arête brillante
    g.fillRect(cx - 1, baseY - hgt, 2, hgt * 0.55)
  }
  // brume horizontale basse : deux bandes dégradées
  for (let i = 0; i < 2; i++) {
    const y0 = H * (0.62 + i * 0.16)
    const fog = g.createLinearGradient(0, y0, 0, y0 + 90)
    fog.addColorStop(0, hexA('#2a2450', 0))
    fog.addColorStop(0.5, hexA('#2a2450', 0.12))
    fog.addColorStop(1, hexA('#2a2450', 0))
    g.fillStyle = fog
    g.fillRect(0, y0, w, 90)
  }
}

// --- Couche MOYENNE (facteur 0.5) : piliers, stalactites, filons, champignons -----------------
function paintMid(g: CanvasRenderingContext2D, w: number): void {
  const rand = mulberry32(SEED + 1)
  // piliers rocheux du sol au plafond (trapèzes, arête claire côté gauche)
  const nPil = 7
  for (let i = 0; i < nPil; i++) {
    const cx = (i + 0.5) * (w / nPil) + (rand() - 0.5) * 160
    const wTop = 34 + rand() * 40, wBot = wTop + 30 + rand() * 46
    const dark = rand() < 0.5 ? '#181228' : '#221838'
    g.fillStyle = dark
    g.beginPath()
    g.moveTo(cx - wTop / 2, 0)
    g.lineTo(cx + wTop / 2, 0)
    g.lineTo(cx + wBot / 2, H)
    g.lineTo(cx - wBot / 2, H)
    g.closePath()
    g.fill()
    g.strokeStyle = hexA('#302448', 0.8)
    g.lineWidth = 3
    g.beginPath()
    g.moveTo(cx - wTop / 2 + 2, 0)
    g.lineTo(cx - wBot / 2 + 2, H)
    g.stroke()
    // filon scintillant sur certains piliers : polyligne dorée fine
    if (i % 2 === 0) {
      g.strokeStyle = hexA('#ffd23f', 0.22)
      g.lineWidth = 2
      g.beginPath()
      let fy = H * (0.2 + rand() * 0.3)
      g.moveTo(cx - wTop / 4, fy)
      for (let s = 0; s < 4; s++) {
        fy += 40 + rand() * 50
        g.lineTo(cx - wTop / 4 + (rand() - 0.5) * 26, fy)
      }
      g.stroke()
    }
  }
  // stalactites du plafond
  g.fillStyle = '#241a3a'
  for (let i = 0; i < 14; i++) {
    const cx = rand() * w
    const hgt = 30 + rand() * 60
    g.beginPath()
    g.moveTo(cx - 10 - rand() * 8, 0)
    g.lineTo(cx + 10 + rand() * 8, 0)
    g.lineTo(cx, hgt)
    g.closePath()
    g.fill()
  }
  // champignons lumineux au pied des piliers
  for (let i = 0; i < 6; i++) {
    const cx = rand() * w
    const cy = H * (0.82 + rand() * 0.12)
    const r = 5 + rand() * 4
    const halo = g.createRadialGradient(cx, cy, 0, cx, cy, r * 2.4)
    halo.addColorStop(0, hexA('#7cfc9a', 0.12))
    halo.addColorStop(1, hexA('#7cfc9a', 0))
    g.fillStyle = halo
    g.fillRect(cx - r * 2.4, cy - r * 2.4, r * 4.8, r * 4.8)
    g.fillStyle = hexA('#7cfc9a', 0.7)
    g.beginPath()
    g.arc(cx, cy, r, Math.PI, 0)
    g.closePath()
    g.fill()
    g.fillStyle = hexA('#5dd47a', 0.7)
    g.fillRect(cx - 1.5, cy, 3, r * 0.9)
  }
}

// --- Couche PROCHE (facteur 0.8) : étais de mine, lanternes, rails, wagonnet ------------------
// Les positions des lanternes sont mémorisées pour les halos ANIMÉS (dessinés par frame).
const LANTERNS: { x: number; y: number }[] = []
function paintNear(g: CanvasRenderingContext2D, w: number): void {
  const rand = mulberry32(SEED + 2)
  LANTERNS.length = 0
  // Ligne d'appui = le dessus du SOL à l'écran : (13*48 - MINE_CAM_Y) * 3 = 543, constante car la
  // caméra Y est figée. Tout ce qui est peint SOUS cette ligne serait recouvert par le sol monde.
  const groundY = (13 * TSW - MINE_CAM_Y) * MINE_ZOOM
  let n = 0
  for (let x = 120; x < w - 60; x += 340 + rand() * 80, n++) {
    // étai : deux montants + linteau, rainures sombres, clous clairs
    g.fillStyle = '#3a2a18'
    g.fillRect(x, H * 0.16, 16, groundY - H * 0.16)
    g.fillRect(x + 148, H * 0.16, 16, groundY - H * 0.16)
    g.fillRect(x - 14, H * 0.14, 192, 20)
    g.fillStyle = '#241708'
    g.fillRect(x + 6, H * 0.16, 3, groundY - H * 0.16)
    g.fillRect(x + 154, H * 0.16, 3, groundY - H * 0.16)
    g.fillRect(x - 14, H * 0.155, 192, 4)
    g.fillStyle = '#6b5a3a'
    for (const nx of [x + 4, x + 12, x + 152, x + 160]) g.fillRect(nx, H * 0.145 + 6, 3, 3)
    // une lanterne suspendue sous un linteau sur deux
    if (n % 2 === 0) {
      const lx = x + 74 + rand() * 20
      const ly = H * 0.16 + 46
      g.strokeStyle = '#241708'
      g.lineWidth = 3
      g.beginPath()
      g.moveTo(lx, H * 0.17)
      g.lineTo(lx, ly - 16)
      g.stroke()
      g.fillStyle = '#caa062'
      g.fillRect(lx - 9, ly - 18, 18, 26)
      g.fillStyle = '#ffd23f'
      g.fillRect(lx - 5, ly - 13, 10, 15)
      g.fillStyle = '#caa062'
      g.fillRect(lx - 11, ly - 21, 22, 5)
      LANTERNS.push({ x: lx, y: ly - 5 })
    }
  }
  // rails posés SUR le sol (juste au-dessus de la ligne d'appui : rien ne doit passer dessous),
  // avec un wagonnet chargé de minerai
  const rx0 = w * 0.24, rx1 = w * 0.62
  g.fillStyle = '#3a2a18'
  for (let tx = rx0; tx < rx1; tx += 26) g.fillRect(tx, groundY - 14, 6, 14)
  g.strokeStyle = '#4a3a28'
  g.lineWidth = 4
  for (const dy of [-12, -4]) {
    g.beginPath()
    g.moveTo(rx0, groundY + dy)
    g.lineTo(rx1, groundY + dy)
    g.stroke()
  }
  const wx = w * 0.44
  g.fillStyle = '#3a2a18'
  g.fillRect(wx, groundY - 52, 78, 34)
  g.fillStyle = '#241708'
  g.beginPath(); g.arc(wx + 18, groundY - 12, 9, 0, Math.PI * 2); g.fill()
  g.beginPath(); g.arc(wx + 60, groundY - 12, 9, 0, Math.PI * 2); g.fill()
  g.fillStyle = hexA('#ffd23f', 0.8)
  for (let i = 0; i < 7; i++) {
    const gx = wx + 8 + (i * 61) % 62
    g.beginPath(); g.arc(gx, groundY - 54 - (i % 3) * 4, 5, 0, Math.PI * 2); g.fill()
  }
}

// --- caches (peints une fois au premier renderMine) -------------------------------------------
let farLayer: HTMLCanvasElement | null = null
let midLayer: HTMLCanvasElement | null = null
let nearLayer: HTMLCanvasElement | null = null
function ensureLayers(): void {
  if (farLayer) return
  const far = mkLayer(W + SCROLL * 0.2); paintFar(far.g, far.cv.width); farLayer = far.cv
  const mid = mkLayer(W + SCROLL * 0.5); paintMid(mid.g, mid.cv.width); midLayer = mid.cv
  const near = mkLayer(W + SCROLL * 0.8); paintNear(near.g, near.cv.width); nearLayer = near.cv
}

/** Test seulement : force une re-peinture des caches (jamais appelé en jeu). */
export function resetMineLayers(): void { farLayer = midLayer = nearLayer = soilBand = null }

// --- fond : blit des 3 couches + halos animés des lanternes -----------------------------------
// PROFONDEUR : la caverne est ANCRÉE AU MONDE (dy) — en descendant, elle sort par le haut de
// l'écran (on s'enfonce dans la roche, la bande souterraine couvre alors tout).
function drawParallax(ctx: CanvasRenderingContext2D, camXWorld: number, camYWorld: number, nowMs: number): void {
  const sx = camXWorld * MINE_ZOOM
  const dy = -Math.round((camYWorld - MINE_CAM_Y) * MINE_ZOOM)
  if (dy <= -H) return // caverne entièrement sortie de l'écran (étage 3)
  ctx.drawImage(farLayer!, -Math.round(sx * 0.2), dy)
  ctx.drawImage(midLayer!, -Math.round(sx * 0.5), dy)
  ctx.drawImage(nearLayer!, -Math.round(sx * 0.8), dy)
  // halos des lanternes : respiration douce, composés en additif (aucun shadowBlur)
  ctx.save()
  ctx.globalCompositeOperation = 'lighter'
  for (let i = 0; i < LANTERNS.length; i++) {
    const l = LANTERNS[i]!
    const x = l.x - sx * 0.8
    const y = l.y + dy
    if (x < -120 || x > W + 120 || y < -120) continue
    const a = 0.1 + 0.04 * Math.sin(nowMs / 450 + i * 1.7)
    const halo = ctx.createRadialGradient(x, y, 0, x, y, 90)
    halo.addColorStop(0, hexA('#ffd23f', a))
    halo.addColorStop(1, hexA('#ffd23f', 0))
    ctx.fillStyle = halo
    ctx.fillRect(x - 90, y - 90, 180, 180)
  }
  ctx.restore()
}

// --- LE SOUS-SOL UNIFIÉ (Lot 2, étendu PROFONDEUR 21/07) --------------------------------------
// Une bande CONTINUE verticale ET horizontale, peinte une fois en résolution écran
// (4032x1584 : monde y 624 -> 1152) : TROIS bandeaux de sol superposés (strates ondulées +
// incrustations soilFeatures, PALETTE par étage), DEUX galeries de marche entre eux, et le FOND
// (roche -> noir, avec la lueur de LAVE qui monte). La moitié des incrustations est à cheval sur
// les jointures : aucune grille visible. Les trous creusés sont peints par-dessus au rendu.
// --- DRAME (séquence v2 21/07 nuit) : intensité du suspense en cours + case qui gronde (monde).
// Posé par le contrôleur à chaque frame de suspense (setMineDrama), remis à 0 hors suspense.
// Les couches d'ambiance y réagissent : les lucioles FUIENT la case, la brume se rétracte,
// les braises pulsent au rythme du grondement. Cosmétique pur.
let dramaK = 0, dramaWx = 0, dramaWy = 0
export function setMineDrama(k: number, wx: number, wy: number): void { dramaK = k; dramaWx = wx; dramaWy = wy }

const UNDERGROUND_TOP = FLOOR_ROW * TSW // 624 : monde y du dessus du sol É1
const BAND_H = (24 * TSW - UNDERGROUND_TOP) * MINE_ZOOM // 1584 : jusqu'au bas de la salle
const SOIL_SCREEN_Y = (FLOOR_ROW * TSW - MINE_CAM_Y) * MINE_ZOOM // 543 : sol É1 à l'écran, caméra en SURFACE
const DEPTH_FLOOR_ROWS = [13, 15, 17] as const // rangées des 3 sols (miroir mine-scene.DEPTH_FLOORS)
let soilBand: HTMLCanvasElement | null = null

// Palettes par étage : strates [terre, roche, riche], paillettes de la couche riche, teintes de
// gemmes. É1 ambre (la surface dorée), É2 roche bleue profonde (froide), É3 rouge lave (le fond).
const FLOOR_PALETTES = [
  { strata: ['#3a2a1c', '#2a1c12', '#1a1008'], glitter: 'rgba(255,210,63,0.25)', gems: ['#5db4ff', '#b98bff', '#ff5f57', '#7cfc9a'] },
  { strata: ['#1c2438', '#141c2c', '#0e1420'], glitter: 'rgba(124,215,255,0.22)', gems: ['#5db4ff', '#7cd7ff', '#b98bff', '#8fd0ff'] },
  { strata: ['#301410', '#200c08', '#140604'], glitter: 'rgba(255,95,42,0.28)', gems: ['#ff5f57', '#ffd23f', '#ff9a3d', '#ff5f2a'] },
] as const

function paintSoilFeature(g: CanvasRenderingContext2D, f: SoilFeature, rand: () => number, gems: readonly string[]): void {
  const x = f.xWorld * MINE_ZOOM
  const s = f.size * MINE_ZOOM * 0.55 // taille écran contenue dans la rangée creusable
  switch (f.kind) {
    case 'filon': { // veine d'or qui serpente dans la roche, cœur clair
      const y0 = 66 + rand() * 50
      for (const [wdt, col, dy] of [[7, '#e0940f', 0], [4, '#ffd23f', 0], [1.6, '#fff3b0', -1]] as const) {
        g.strokeStyle = col
        g.lineWidth = wdt
        g.beginPath()
        g.moveTo(x - s, y0 + dy)
        g.quadraticCurveTo(x - s * 0.3, y0 - 14 + dy, x, y0 + dy)
        g.quadraticCurveTo(x + s * 0.4, y0 + 12 + dy, x + s, y0 - 6 + dy)
        g.stroke()
      }
      break
    }
    case 'gemme': { // losange facetté serti, reflet vif
      const cy = 60 + rand() * 56
      const tint = gems[f.tint]!
      g.fillStyle = 'rgba(6,4,14,0.6)'
      g.beginPath(); g.ellipse(x, cy, s * 0.75, s * 0.6, 0, 0, Math.PI * 2); g.fill()
      g.fillStyle = tint
      g.beginPath(); g.moveTo(x, cy - s * 0.55); g.lineTo(x + s * 0.45, cy); g.lineTo(x, cy + s * 0.55); g.lineTo(x - s * 0.45, cy); g.closePath(); g.fill()
      g.fillStyle = 'rgba(255,255,255,0.55)'
      g.beginPath(); g.moveTo(x, cy - s * 0.55); g.lineTo(x + s * 0.45, cy); g.lineTo(x, cy); g.closePath(); g.fill()
      g.fillStyle = 'rgba(255,255,255,0.9)'
      g.fillRect(x - s * 0.1, cy - s * 0.34, 3, 3)
      break
    }
    case 'fossile': { // ammonite : arcs concentriques gris clair
      const cy = 74 + rand() * 40
      g.strokeStyle = 'rgba(203,196,222,0.5)'
      g.lineWidth = 2.5
      for (let r = s * 0.16; r <= s * 0.55; r += s * 0.13) {
        g.beginPath(); g.arc(x, cy, r, rand() * 0.9, Math.PI * (1.4 + rand() * 0.5)); g.stroke()
      }
      break
    }
    case 'racine': { // racines qui descendent de la surface du sol
      g.strokeStyle = '#4a3a28'
      for (let b = 0; b < 3; b++) {
        g.lineWidth = 4 - b
        g.beginPath()
        g.moveTo(x + (b - 1) * 8, 2)
        g.quadraticCurveTo(x + (b - 1) * 20 + (rand() - 0.5) * 16, 40 + rand() * 20, x + (b - 1) * 26, 70 + rand() * 46)
        g.stroke()
      }
      break
    }
    case 'crane': { // vieux crâne enfoui, discret
      const cy = 84 + rand() * 34
      g.fillStyle = 'rgba(203,196,222,0.42)'
      g.beginPath(); g.arc(x, cy, s * 0.5, Math.PI, 0); g.fill()
      g.fillRect(x - s * 0.42, cy, s * 0.84, s * 0.3)
      g.fillStyle = 'rgba(6,4,14,0.8)'
      g.beginPath(); g.arc(x - s * 0.2, cy - s * 0.06, s * 0.12, 0, Math.PI * 2); g.fill()
      g.beginPath(); g.arc(x + s * 0.2, cy - s * 0.06, s * 0.12, 0, Math.PI * 2); g.fill()
      break
    }
    case 'rail': { // tronçon de rail englouti, incliné (vestige d'une vieille galerie)
      const cy = 92 + rand() * 30
      const tilt = (rand() - 0.5) * 0.5
      g.save()
      g.translate(x, cy)
      g.rotate(tilt)
      g.strokeStyle = 'rgba(107,74,42,0.85)'
      g.lineWidth = 3.5
      for (const dy of [-5, 5]) { g.beginPath(); g.moveTo(-s, dy); g.lineTo(s, dy); g.stroke() }
      g.fillStyle = 'rgba(58,42,24,0.85)'
      for (let tx = -s + 6; tx < s - 4; tx += 16) g.fillRect(tx, -9, 5, 18)
      g.restore()
      break
    }
  }
}

/** Un BANDEAU de sol (144 px écran = une rangée creusable) à l'offset `yOff` de la bande :
 *  strates ondulées de la palette, grain, paillettes, incrustations, sertissage doré. */
function paintFloorBand(
  g: CanvasRenderingContext2D, w: number, yOff: number,
  pal: (typeof FLOOR_PALETTES)[number], seedOff: number,
): void {
  g.save()
  g.translate(0, yOff)
  g.beginPath()
  g.rect(0, 0, w, 144)
  g.clip() // les strates/incrustations restent DANS le bandeau
  const rand = mulberry32(SEED + 3 + seedOff)
  const wave = (baseY: number, amp: number, sOff: number): [number, number][] => {
    const r2 = mulberry32(SEED + sOff + seedOff * 7)
    const pts: [number, number][] = []
    for (let x = 0; x <= w + 48; x += 48) pts.push([x, baseY + (r2() - 0.5) * amp * 2])
    return pts
  }
  const fillBelow = (pts: [number, number][], toY: number, col: string): void => {
    g.fillStyle = col
    g.beginPath()
    g.moveTo(0, toY)
    g.lineTo(pts[0]![0], pts[0]![1])
    for (const [px, py] of pts) g.lineTo(px, py)
    g.lineTo(w, toY)
    g.closePath()
    g.fill()
  }
  fillBelow(wave(0, 0, 30), 150, pal.strata[0]!) // terre (depuis le dessus du sol)
  fillBelow(wave(52, 9, 31), 150, pal.strata[1]!) // roche
  fillBelow(wave(100, 11, 32), 150, pal.strata[2]!) // couche riche
  g.fillStyle = 'rgba(74,58,40,0.4)' // grain
  for (let i = 0; i < 120; i++) g.fillRect(rand() * w, 8 + rand() * 92, 3 + rand() * 4, 2 + rand() * 3)
  g.fillStyle = pal.glitter // paillettes de la couche riche
  for (let i = 0; i < 90; i++) g.fillRect(rand() * w, 102 + rand() * 40, 2, 2)
  // les incrustations UNIQUES de CET étage (layout pur, moitié à cheval sur les jointures)
  for (const f of soilFeatures(SEED + seedOff * 50)) paintSoilFeature(g, f, rand, pal.gems)
  // sertissage : liseré d'or dégradé sur les cases creusables, l'affordance « creuse »
  const gild = g.createLinearGradient(0, 0, 0, 9)
  gild.addColorStop(0, 'rgba(255,210,63,0.5)')
  gild.addColorStop(1, 'rgba(255,210,63,0)')
  g.fillStyle = gild
  g.fillRect(TSW * MINE_ZOOM, 0, (COLS - 2) * TSW * MINE_ZOOM, 9)
  g.restore()
}

// --- DÉCORS DE GALERIE (26/07, demande Claude-Alain « il manque toujours de décors ») ------------
// Les deux galeries profondes — l'espace où le joueur MARCHE aux étages 2 et 3 — n'avaient qu'un
// fond dégradé, deux poutres et quelques cailloux, quand la surface a ses étais, ses lanternes,
// ses rails et son wagonnet. On descendait dans une pièce plus pauvre que celle qu'on quittait.
// Chaque étage reçoit donc SON monde, pour qu'on sache où on est sans lire le bandeau :
//   étage 2, la GROTTE DE CRISTAL : draperies bleues, cristaux qui poussent, flaque, champignons ;
//   étage 3, LA FORGE ÉTEINTE : fissures incandescentes, ossements, chaînes, enclume abandonnée.
// Tout est peint UNE FOIS dans la bande pré-cuite (aucun coût par frame) et reste en arrière-plan.

/** Stalactite / stalagmite : triangle effilé avec une arête claire. `dir` -1 = pend du plafond. */
function spike(g: CanvasRenderingContext2D, x: number, y: number, h: number, wdt: number, dir: 1 | -1, col: string, hi: string): void {
  g.fillStyle = col
  g.beginPath()
  g.moveTo(x - wdt, y)
  g.lineTo(x + wdt, y)
  g.lineTo(x, y + h * dir * -1)
  g.closePath()
  g.fill()
  g.fillStyle = hi
  g.beginPath()
  g.moveTo(x - wdt * 0.35, y)
  g.lineTo(x, y)
  g.lineTo(x, y + h * dir * -1)
  g.closePath()
  g.fill()
}

/** Grappe de cristaux : 3 à 5 prismes qui poussent du sol (ou du plafond), teinte donnée. */
function crystalCluster(g: CanvasRenderingContext2D, x: number, y: number, ech: number, dir: 1 | -1, teinte: string, coeur: string, rand: () => number): void {
  const n = 3 + Math.floor(rand() * 3)
  for (let i = 0; i < n; i++) {
    const dx = (i - (n - 1) / 2) * 9 * ech + (rand() - 0.5) * 5
    const h = (16 + rand() * 22) * ech
    spike(g, x + dx, y, h, (2.6 + rand() * 2) * ech, dir, teinte, coeur)
  }
}

/** Une GALERIE de marche (144 px) : fond de roche, poutres, pierres, et le décor propre à l'étage. */
function paintGallery(g: CanvasRenderingContext2D, w: number, yOff: number, depth: number): void {
  const rand = mulberry32(SEED + 60 + depth)
  const froid = depth === 2
  const bg = g.createLinearGradient(0, yOff, 0, yOff + 144)
  bg.addColorStop(0, froid ? '#131026' : '#100a1c')
  bg.addColorStop(1, froid ? '#1a1430' : '#150d22')
  g.fillStyle = bg
  g.fillRect(0, yOff, w, 144)
  g.fillStyle = 'rgba(0,0,0,0.55)' // lèvre d'ombre sous le sol du dessus (la profondeur se lit)
  g.fillRect(0, yOff, w, 12)
  const sol = yOff + 138 // ligne d'appui : le pied de la galerie

  // Draperies de paroi : de longues coulées verticales qui donnent du relief au fond plat.
  for (let i = 0; i < 26; i++) {
    const x = rand() * w
    const h = 30 + rand() * 80
    g.fillStyle = froid ? `rgba(90,130,190,${0.05 + rand() * 0.07})` : `rgba(150,70,45,${0.05 + rand() * 0.08})`
    g.fillRect(x, yOff + 10, 3 + rand() * 9, h)
  }
  // Stalactites du plafond de la galerie, sur toute la longueur.
  for (let x = 30; x < w; x += 70 + rand() * 120) {
    spike(g, x, yOff + 12, 14 + rand() * 26, 5 + rand() * 4, -1,
      froid ? '#243458' : '#341a14', froid ? '#38507e' : '#4a2418')
  }

  for (let x = 160 + rand() * 120; x < w - 60; x += 320 + rand() * 90) { // poutres du fond de galerie
    g.fillStyle = '#241708'
    g.fillRect(x, yOff + 8, 9, 136)
    g.fillStyle = '#3a2a18'
    g.fillRect(x, yOff + 8, 3, 136)
  }

  if (froid) {
    // ÉTAGE 2 — LA GROTTE DE CRISTAL. Bleue, silencieuse, minérale.
    for (let x = 90; x < w - 60; x += 210 + rand() * 190) {
      crystalCluster(g, x, sol, 1 + rand() * 0.7, 1, '#2e6ea8', '#7cd7ff', rand)  // grappes au sol
    }
    for (let x = 200; x < w - 60; x += 330 + rand() * 240) {
      crystalCluster(g, x, yOff + 14, 0.65 + rand() * 0.4, -1, '#27527e', '#5db4ff', rand) // au plafond
    }
    // Flaques d'eau immobile : ellipse sombre + reflet clair, l'eau qui suinte de la roche.
    for (let i = 0; i < 4; i++) {
      const px = 150 + rand() * (w - 300), pw = 40 + rand() * 70
      g.fillStyle = 'rgba(40,90,150,0.35)'
      g.beginPath(); g.ellipse(px, sol + 2, pw, 5, 0, 0, Math.PI * 2); g.fill()
      g.fillStyle = 'rgba(160,220,255,0.22)'
      g.beginPath(); g.ellipse(px - pw * 0.2, sol, pw * 0.45, 1.8, 0, 0, Math.PI * 2); g.fill()
    }
    // Champignons luminescents : pied pâle, chapeau cyan, halo. Taille lisible face au héros
    // (144 px de haut ici) : un champignon de 8 px passait inaperçu.
    for (let i = 0; i < 14; i++) {
      const mx = rand() * w, mh = 16 + rand() * 20
      g.fillStyle = '#8aa8c8'
      g.fillRect(mx, sol - mh, 5, mh)
      g.fillStyle = '#6afcff'
      g.beginPath(); g.ellipse(mx + 2.5, sol - mh, 11 + rand() * 7, 7.5, 0, Math.PI, 0); g.fill()
      g.fillStyle = 'rgba(255,255,255,0.5)' // pastilles claires du chapeau
      g.beginPath(); g.arc(mx - 2, sol - mh - 2.5, 1.8, 0, Math.PI * 2); g.fill()
      g.beginPath(); g.arc(mx + 7, sol - mh - 4, 1.4, 0, Math.PI * 2); g.fill()
      const halo = g.createRadialGradient(mx + 2, sol - mh, 0, mx + 2, sol - mh, 34)
      halo.addColorStop(0, 'rgba(106,252,255,0.22)'); halo.addColorStop(1, 'rgba(106,252,255,0)')
      g.fillStyle = halo
      g.fillRect(mx - 32, sol - mh - 34, 68, 68)
    }
    // COLONNES de cristal : une stalactite et une stalagmite qui se rejoignent, la silhouette
    // qui dit « grotte » d'un seul coup d'œil. Rares (deux ou trois), donc précieuses.
    for (let i = 0; i < 3; i++) {
      const cx = 220 + rand() * (w - 440)
      const larg = 9 + rand() * 6
      g.fillStyle = '#2e6ea8'
      g.beginPath()
      g.moveTo(cx - larg, yOff + 12); g.lineTo(cx + larg, yOff + 12)
      g.lineTo(cx + larg * 0.55, sol); g.lineTo(cx - larg * 0.55, sol)
      g.closePath(); g.fill()
      g.fillStyle = 'rgba(124,215,255,0.5)'
      g.fillRect(cx - larg * 0.45, yOff + 12, larg * 0.5, sol - yOff - 12)
      const lueur = g.createLinearGradient(cx - 30, 0, cx + 30, 0)
      lueur.addColorStop(0, 'rgba(93,180,255,0)')
      lueur.addColorStop(0.5, 'rgba(93,180,255,0.13)')
      lueur.addColorStop(1, 'rgba(93,180,255,0)')
      g.fillStyle = lueur
      g.fillRect(cx - 30, yOff + 12, 60, sol - yOff - 12)
    }
  } else {
    // ÉTAGE 3 — LA FORGE ÉTEINTE. Le fond : ce que la mine a laissé derrière elle.
    // Fissures incandescentes : la lave respire derrière la paroi.
    for (let i = 0; i < 9; i++) {
      const fx = rand() * w, fy = yOff + 20 + rand() * 90
      const seg = 3 + Math.floor(rand() * 3)
      let cx = fx, cy = fy
      g.strokeStyle = 'rgba(255,95,42,0.5)'
      g.lineWidth = 2.4
      g.beginPath(); g.moveTo(cx, cy)
      for (let s = 0; s < seg; s++) { cx += (rand() - 0.5) * 40; cy += 10 + rand() * 20; g.lineTo(cx, cy) }
      g.stroke()
      g.strokeStyle = 'rgba(255,210,63,0.55)'
      g.lineWidth = 0.9
      g.stroke()
    }
    // Ossements au sol : côtes, et de loin en loin un crâne. ÉCHELLE : le héros fait 144 px de haut
    // dans cette bande — un crâne de 14 px serait une poussière. Ils font ici ~1/4 de sa hauteur.
    for (let i = 0; i < 7; i++) {
      const ox = 120 + rand() * (w - 240)
      g.fillStyle = '#c8bda0'
      if (rand() < 0.4) { // crâne
        g.beginPath(); g.ellipse(ox, sol - 20, 20, 16, 0, 0, Math.PI * 2); g.fill()
        g.fillRect(ox - 11, sol - 12, 22, 13)                                  // mâchoire
        g.fillStyle = '#1a1008'
        g.fillRect(ox - 11, sol - 25, 8, 8); g.fillRect(ox + 3, sol - 25, 8, 8) // orbites
        g.fillRect(ox - 2.5, sol - 14, 5, 5)                                    // nez
        g.fillStyle = '#8a8168'
        for (let k = 0; k < 3; k++) g.fillRect(ox - 9 + k * 7, sol - 6, 4, 6)   // dents
      } else { // cage thoracique
        for (let k = 0; k < 5; k++) {
          g.beginPath()
          g.ellipse(ox + k * 15, sol - 12, 5, 15 - k * 1.4, 0.22, 0, Math.PI * 2)
          g.fill()
        }
        g.fillStyle = '#a89d80' // colonne
        g.fillRect(ox - 6, sol - 4, 76, 5)
        g.fillStyle = '#c8bda0'
      }
    }
    // Chaînes rouillées pendues au plafond, maillon par maillon, jusqu'à mi-galerie.
    for (let i = 0; i < 5; i++) {
      const cx = 100 + rand() * (w - 200), n = 5 + Math.floor(rand() * 6)
      for (let k = 0; k < n; k++) {
        g.strokeStyle = k % 2 ? '#6b4a2a' : '#8a6329'
        g.lineWidth = 4
        g.beginPath(); g.ellipse(cx, yOff + 20 + k * 17, 6.5, 9, 0, 0, Math.PI * 2); g.stroke()
      }
    }
    // L'ENCLUME abandonnée sur son billot : la pièce maîtresse du fond, à hauteur de genou du héros.
    const ax = w * 0.38 + rand() * w * 0.2
    g.fillStyle = '#241708'
    g.fillRect(ax - 30, sol - 34, 60, 34)          // billot
    g.fillStyle = '#1a1008'
    for (let k = 0; k < 3; k++) g.fillRect(ax - 30, sol - 30 + k * 11, 60, 2) // cernes du bois
    g.fillStyle = '#3a3a44'
    g.fillRect(ax - 38, sol - 52, 76, 15)          // table de l'enclume
    g.fillRect(ax - 15, sol - 40, 30, 9)           // pied
    g.beginPath()                                   // corne
    g.moveTo(ax + 38, sol - 52); g.lineTo(ax + 66, sol - 45); g.lineTo(ax + 38, sol - 37)
    g.closePath(); g.fill()
    g.fillStyle = '#5a5a68'
    g.fillRect(ax - 38, sol - 52, 76, 4)           // arête claire
    // Le marteau planté dans le billot, et des braises éteintes au pied.
    g.strokeStyle = '#5a3d1c'; g.lineWidth = 5
    g.beginPath(); g.moveTo(ax - 14, sol - 34); g.lineTo(ax - 22, sol - 74); g.stroke()
    g.fillStyle = '#4a4a56'
    g.fillRect(ax - 33, sol - 84, 24, 13)
    for (let i = 0; i < 14; i++) {
      const bx = ax - 90 + rand() * 180
      g.fillStyle = rand() < 0.35 ? 'rgba(255,95,42,0.5)' : 'rgba(60,40,34,0.9)'
      g.fillRect(bx, sol - 6 - rand() * 5, 6 + rand() * 6, 4 + rand() * 3)
    }
  }

  g.fillStyle = froid ? '#1a2234' : '#241010' // pierres éparses au pied
  for (let i = 0; i < 16; i++) g.fillRect(rand() * w, yOff + 116 + rand() * 22, 5 + rand() * 8, 3 + rand() * 5)
}

/** La bande souterraine COMPLÈTE : 3 sols + 2 galeries + le fond avec sa lueur de lave. */
function paintUnderground(g: CanvasRenderingContext2D, w: number): void {
  // le fond (sous le sol É3) : roche qui s'enfonce dans le noir... éclairée par la LAVE
  const deep = g.createLinearGradient(0, 720, 0, BAND_H)
  deep.addColorStop(0, '#141019')
  deep.addColorStop(1, '#060410')
  g.fillStyle = deep
  g.fillRect(0, 0, w, BAND_H)
  // lueur de lave : elle doit vivre dans la ZONE VISIBLE sous le sol É3 (la caméra du fond ne
  // descend jamais sous ~y monde 907 -> bande ~850) — le rouge monte dès la fin du sol du fond
  const lava = g.createLinearGradient(0, 726, 0, 1030)
  lava.addColorStop(0, 'rgba(255,95,42,0)')
  lava.addColorStop(1, 'rgba(255,95,42,0.3)')
  g.fillStyle = lava
  g.fillRect(0, 726, w, BAND_H - 726)
  const rand = mulberry32(SEED + 80)
  g.strokeStyle = 'rgba(255,95,42,0.5)' // veines incandescentes, juste sous le sol du fond
  g.lineWidth = 3
  for (let i = 0; i < 8; i++) {
    let vx = rand() * w
    let vy = 750 + rand() * 150
    g.beginPath()
    g.moveTo(vx, vy)
    for (let s2 = 0; s2 < 4; s2++) { vx += (rand() - 0.5) * 130; vy += 8 + rand() * 26; g.lineTo(vx, vy) }
    g.stroke()
  }
  g.fillStyle = '#1e1830' // cailloux épars du fond
  for (let i = 0; i < 30; i++) g.fillRect(rand() * w, 740 + rand() * 800, 5 + rand() * 7, 3 + rand() * 5)
  // les étages, de la surface vers le fond : sol É1 / galerie É2 / sol É2 / galerie É3 / sol É3
  paintFloorBand(g, w, 0, FLOOR_PALETTES[0]!, 0)
  paintGallery(g, w, 144, 2)
  paintFloorBand(g, w, 288, FLOOR_PALETTES[1]!, 1)
  paintGallery(g, w, 432, 3)
  paintFloorBand(g, w, 576, FLOOR_PALETTES[2]!, 2)
}

function ensureSoilBand(): void {
  if (soilBand) return
  const band = mkLayer(COLS * TSW * MINE_ZOOM)
  band.cv.height = BAND_H
  paintUnderground(band.g, band.cv.width)
  soilBand = band.cv
}

/** Blit écran-space de la fenêtre caméra (X ET Y : la caméra plonge) + étincelles des sertissages
 *  de chaque sol visible. */
function drawSoilBand(ctx: CanvasRenderingContext2D, camXWorld: number, camYWorld: number, nowMs: number): void {
  const dy = (UNDERGROUND_TOP - camYWorld) * MINE_ZOOM
  ctx.drawImage(soilBand!, camXWorld * MINE_ZOOM, 0, W, BAND_H, 0, dy, W, BAND_H)
  ctx.save()
  ctx.globalCompositeOperation = 'lighter'
  ctx.fillStyle = '#fff3b0'
  for (let d = 0; d < DEPTH_FLOOR_ROWS.length; d++) {
    const sy = (DEPTH_FLOOR_ROWS[d]! * TSW - camYWorld) * MINE_ZOOM
    if (sy < -6 || sy > H + 6) continue
    for (let i = 0; i < 6; i++) {
      const wx = 48 + ((i * 211 + 37 + d * 97) % 1248)
      const sx = (wx - camXWorld) * MINE_ZOOM
      if (sx < -6 || sx > W + 6) continue
      const a = Math.sin(nowMs / 300 + i * 2.1 + d)
      if (a <= 0) continue
      ctx.globalAlpha = a * 0.8
      ctx.fillRect(sx - 1.5, sy - 1, 3, 3)
    }
  }
  ctx.restore()
  ctx.globalAlpha = 1
}

// --- cavités des cases creusées + murs + échelle (espace MONDE, par-dessus la bande) ----------
function drawSoil(ctx: CanvasRenderingContext2D, cur: GameState): void {
  // les trous creusés, sur CHACUN des 3 sols (col 1 = l'échelle, jamais creusable). La REPOUSSE
  // se voit : la brique « remonte » du fond du trou par stades (regrowStage) — on lit d'un coup
  // d'œil qu'il faut sauter MAINTENANT ou jamais.
  for (const floor of DEPTH_FLOOR_ROWS) {
    const y = floor * TSW
    for (let col = 2; col < LAST_COL; col++) {
      const cell = floor * cur.cols + col
      if (cur.terrain[cell] === T.Brick) continue
      const x = col * TSW
      ctx.fillStyle = '#0a0714' // cavité ouverte : sombre (elle donne sur la galerie du dessous)
      ctx.fillRect(x, y, TSW, TSW)
      ctx.fillStyle = '#241708' // parois latérales taillées
      ctx.fillRect(x, y, 3, TSW)
      ctx.fillRect(x + TSW - 3, y, 3, TSW)
      const hole = cur.holes.find((h) => h.cell === cell)
      if (hole && regrowStage(cur, hole) === 1) { // la repousse arrive : la brique remonte du fond
        const pal = FLOOR_PALETTES[DEPTH_FLOOR_ROWS.indexOf(floor)]!
        ctx.fillStyle = pal.strata[1]!
        ctx.fillRect(x + 3, y + TSW * 0.55, TSW - 6, TSW * 0.45)
        ctx.fillStyle = pal.strata[0]!
        ctx.fillRect(x + 3, y + TSW * 0.55, TSW - 6, 4)
      }
      const lip = ctx.createLinearGradient(0, y, 0, y + 10) // lèvre d'ombre : la profondeur se lit
      lip.addColorStop(0, 'rgba(0,0,0,0.65)')
      lip.addColorStop(1, 'rgba(0,0,0,0)')
      ctx.fillStyle = lip
      ctx.fillRect(x, y, TSW, 10)
    }
  }
  // murs latéraux de la salle (colonnes 0 et 27), sur TOUTE la profondeur : roche stratifiée
  // sombre, arête claire côté salle, quelques fissures — un aplat uni jurerait à côté du reste
  const wrand = mulberry32(SEED + 4)
  for (const x of [0, LAST_COL * TSW]) {
    ctx.fillStyle = '#1c1530'
    ctx.fillRect(x, 0, TSW, 24 * TSW)
    ctx.fillStyle = '#221a3a'
    for (let sy = 0; sy < 24 * TSW; sy += 34) ctx.fillRect(x, sy + (sy % 68 === 0 ? 0 : 8), TSW, 12)
    ctx.fillStyle = '#0f0b1e'
    for (let i = 0; i < 10; i++) {
      const fy = wrand() * 23 * TSW
      ctx.fillRect(x + 6 + wrand() * (TSW - 18), fy, 2, 14 + wrand() * 20)
    }
    ctx.fillStyle = '#302448'
    ctx.fillRect(x + (x === 0 ? TSW - 4 : 0), 0, 4, 24 * TSW)
  }
  // l'échelle de remontée (col 1, rows 13-16) : montants + barreaux de bois
  const lx = TSW
  ctx.fillStyle = '#3a2a18'
  ctx.fillRect(lx + 8, 13 * TSW, 4, 4 * TSW)
  ctx.fillRect(lx + 36, 13 * TSW, 4, 4 * TSW)
  ctx.fillStyle = '#6b4a2a'
  for (let by = 13 * TSW + 6; by < 17 * TSW; by += 12) ctx.fillRect(lx + 8, by, 32, 3)
}

// --- héros ×3 : pose locale (règle PURE partagée, cf. mine-scene.minePose) ---------------------
// 26/07 : le dash et l'échelle manquaient à ce sélecteur — la salle est pourtant créée avec
// dashEnabled:true, et on remonte par l'échelle après chaque descente. Le dash s'exécutait donc
// sans jamais se voir. Les GROS effets (comète, foudre, ondes) sont ceux du jeu (client/dash-fx),
// dessinés en espace MONDE de part et d'autre du héros, à la même échelle (48 px/case).
function lerp(a: number, b: number, t: number): number { return a + (b - a) * t }
function drawHero(
  ctx: CanvasRenderingContext2D, prev: GameState | null, cur: GameState, alpha: number,
  digUntil: number[], nowMs: number,
): void {
  const r = cur.runners[0]
  if (!r || !r.alive) return
  const p = prev?.runners[0]
  const px = lerp(p ? p.x : r.x, r.x, alpha) * PXW
  const py = lerp(p ? p.y : r.y, r.y, alpha) * PXW
  const moving = p ? p.x !== r.x : false
  const digLeft = (digUntil[0] ?? 0) - cur.tick
  const t = cur.tick + alpha
  const pose = minePose({
    tick: cur.tick, falling: r.falling, moving, onLadder: r.onLadder, digLeft,
    dashChargeUntil: r.dashChargeUntil, dashUntil: r.dashUntil,
  })
  // Phases : le dash suit la fenêtre de burst (miroir de baroudeurPhase), le reste bat à l'horloge.
  const phase = pose === 'dash' ? 0.22 + Math.max(0, Math.min(1, (t - r.dashChargeUntil) / 6)) * 0.20
    : pose === 'fall' ? (nowMs / 700) % 1
      : pose === 'climb' ? (py / 30) % 1  // grimpe : la cadence suit la montée réelle
        : pose === 'run' ? (px / 44) % 1  // cycle ~1 case : la cadence des jambes suit le sol
          : pose === 'idle' ? (nowMs / 1400) % 1
            : 0                            // crouch / brake / pioche : poses figées
  // Variante d'effet du dash, tirée comme dans le jeu (pure, dérivée de dashUntil) : la Mine hérite
  // des trois grandes animations plutôt que d'en avoir une pâle copie.
  const dv = r.dashUntil > 0 ? dashVariant(r.dashUntil, 0) : 0
  dashFxTrack(0, r.dashUntil, r.dashChargeUntil, px, py, r.face < 0 ? -1 : 1, hueIndex(avatarHue()), t)
  drawDashFxBack(ctx, t) // queue de comète, échos, ondes de départ : SOUS le héros
  // la LAMPE FRONTALE du Baroudeur : halo chaud discret autour de lui — décoratif en surface,
  // vital dans le noir des galeries profondes
  ctx.save()
  ctx.globalCompositeOperation = 'lighter'
  const lamp = ctx.createRadialGradient(px + TSW / 2, py + TSW * 0.4, 0, px + TSW / 2, py + TSW * 0.4, 92)
  lamp.addColorStop(0, 'rgba(255,210,63,0.11)')
  lamp.addColorStop(1, 'rgba(255,210,63,0)')
  ctx.fillStyle = lamp
  ctx.fillRect(px + TSW / 2 - 92, py + TSW * 0.4 - 92, 184, 184)
  ctx.restore()
  // ÉCLAIR D'ARCANE (variante 1) : pendant le burst, le corps EST la foudre — le dessiner en plus
  // ferait un héros fantôme en double.
  if (!dashFxHideBody(0, r.dashUntil, t)) {
    // EMOTE en cours (26/07) : le corps rebondit et penche autour de ses PIEDS (pivot au sol, pour
    // qu'une danse ne fasse jamais décoller le héros). L'animation est une fonction pure du temps :
    // elle revient d'elle-même au repos, donc aucune emote ne peut laisser le héros de travers.
    const em = currentEmote(nowMs)
    const bob = em ? em.frame.bob * TSW : 0
    if (em && em.frame.tilt !== 0) {
      ctx.save()
      ctx.translate(px + TSW / 2, py + TSW)
      ctx.rotate(em.frame.tilt)
      ctx.translate(-(px + TSW / 2), -(py + TSW))
    }
    // La TENUE équipée (26/07) : la Mine est l'endroit où on la gagne et où on l'essaie, elle doit
    // donc s'y voir en premier. `equipped` renvoie null hors ligne -> Baroudeur d'origine.
    drawBaroudeur(ctx, pose as PoseName, phase, r.face < 0 ? -1 : 1, px, py + bob, TSW, { hue: hueIndex(avatarHue()), dashVariant: dv, outfit: equipped('outfit') ?? '', hat: equipped('hat') ?? '', hatTime: t })
    if (em && em.frame.tilt !== 0) ctx.restore()
  }
  drawEmoteBubble(ctx, px, py, TSW, nowMs) // la bulle : au-dessus de la tête, jamais penchée
  drawDashFxFront(ctx, t) // foudre, runes, arcs, étoiles d'arrivée : PAR-DESSUS
}

// --- invite « creuse » : écran-space (texte net), au-dessus de la tête du héros ---------------
function drawHint(
  ctx: CanvasRenderingContext2D, cur: GameState, cam: MineCam, nowMs: number, hasBricks: boolean, touch: boolean, cost: number,
  depth = 1,
): void {
  const r = cur.runners[0]
  if (!r || !r.alive || r.falling) return
  const wx = r.x * PXW + TSW / 2
  const wy = r.y * PXW
  const sx = (wx - cam.x) * cam.zoom
  const sy = (wy - cam.y) * cam.zoom
  const bob = Math.sin(nowMs / 220) * 3
  const price = cost > 1 ? ` (${cost} 🧱)` : '' // profondeur : le coût de l'étage s'affiche
  const text = hasBricks ? (touch ? `⛏ Creuse le sol${price}` : `⛏ A / S : creuse${price}`) : '🧱 Boutique'
  // CE QU'ON Y TROUVE (26/07) : sous l'invite, la promesse de l'étage visé. Sans elle, le coût
  // supplémentaire des étages profonds se lit comme une punition au lieu d'une offre.
  const promesse = hasBricks && depth > 1 ? depthPromise(depth) : ''
  ctx.font = 'bold 20px ui-monospace, Menlo, monospace'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'alphabetic'
  const tw = ctx.measureText(text).width
  ctx.font = 'bold 14px ui-monospace, Menlo, monospace'
  const pw = promesse ? ctx.measureText(promesse).width : 0
  const larg = Math.max(tw, pw) + 24
  const haut = promesse ? 48 : 30
  ctx.fillStyle = 'rgba(10,7,20,0.72)'
  const bx = sx - larg / 2, by = sy - (promesse ? 64 : 46) + bob
  ctx.beginPath()
  ctx.roundRect(bx, by, larg, haut, 10)
  ctx.fill()
  ctx.font = 'bold 20px ui-monospace, Menlo, monospace'
  ctx.fillStyle = hasBricks ? '#ffd23f' : '#9a93c0'
  ctx.fillText(text, sx, by + 21)
  if (promesse) {
    ctx.font = 'bold 14px ui-monospace, Menlo, monospace'
    ctx.fillStyle = depth >= 3 ? '#ff9a3d' : '#7cd7ff'
    ctx.fillText(promesse, sx, by + 40)
  }
}

// --- AMBIANCE VIVANTE (Lot 4) : poussières, lucioles, brume, rayons, vignette -----------------
// Tout est borné et sans allocation par frame (positions dérivées de l'horloge + index) ; la
// vignette est bakée une fois. Les poussières/lucioles vivent en espace MONDE (elles défilent
// avec la caméra : la profondeur se sent) ; brume et rayons en écran-space (voiles de lumière).
function drawDust(ctx: CanvasRenderingContext2D, nowMs: number): void {
  ctx.save()
  ctx.globalCompositeOperation = 'lighter'
  ctx.fillStyle = '#ffd23f'
  for (let i = 0; i < 26; i++) {
    const x = (i * 97 + 13 + nowMs * 0.006) % 1344
    const y = 452 + ((i * 53) % 158) + 8 * Math.sin(nowMs / 2600 + i)
    ctx.globalAlpha = 0.1 + 0.12 * (0.5 + 0.5 * Math.sin(nowMs / 900 + i * 1.3))
    ctx.fillRect(x, y, 1.3, 1.3)
  }
  ctx.restore()
  ctx.globalAlpha = 1
}

function drawFireflies(ctx: CanvasRenderingContext2D, nowMs: number): void {
  ctx.save()
  ctx.globalCompositeOperation = 'lighter'
  for (let i = 0; i < 7; i++) {
    let x = ((i * 181 + 60) % 1300) + 26 * Math.sin(nowMs / 1700 + i * 2.1)
    let y = 470 + ((i * 71) % 130) + 18 * Math.sin(nowMs / 2300 + i * 1.4 + 1)
    if (dramaK > 0) { // fuite : poussées loin de la case qui gronde (falloff doux)
      const ddx = x - dramaWx, ddy = y - dramaWy
      const dd = Math.hypot(ddx, ddy) + 1
      const push = dramaK * 5200 / (dd + 90)
      x += (ddx / dd) * push
      y += (ddy / dd) * push * 0.5
    }
    const a = (0.15 + 0.35 * (0.5 + 0.5 * Math.sin(nowMs / 640 + i * 2.6))) * (1 - dramaK * 0.3)
    const halo = ctx.createRadialGradient(x, y, 0, x, y, 10)
    halo.addColorStop(0, hexA('#7cfc9a', a))
    halo.addColorStop(1, hexA('#7cfc9a', 0))
    ctx.fillStyle = halo
    ctx.fillRect(x - 10, y - 10, 20, 20)
    ctx.globalAlpha = Math.min(1, a + 0.3)
    ctx.fillStyle = '#c8ffd8'
    ctx.fillRect(x - 0.7, y - 0.7, 1.4, 1.4)
    ctx.globalAlpha = 1
  }
  ctx.restore()
}

/** Estompage des effets de SURFACE quand la caméra descend (brume/rayons appartiennent au haut). */
function surfaceFade(camYWorld: number): number {
  return Math.max(0, 1 - (camYWorld - MINE_CAM_Y) / 230)
}

function drawFog(ctx: CanvasRenderingContext2D, camYWorld: number, nowMs: number): void {
  const fade = surfaceFade(camYWorld)
  if (fade <= 0) return
  const soilY = (UNDERGROUND_TOP - camYWorld) * MINE_ZOOM // le sol É1 à l'écran (ancré monde)
  for (let row = 0; row < 2; row++) {
    const y = soilY - 14 - row * 26
    const alpha = (row === 0 ? 0.09 : 0.06) * fade * (1 - dramaK * 0.6) // le drame aspire la brume
    ctx.fillStyle = hexA('#4a4470', alpha)
    for (let i = 0; i < 5; i++) {
      const x = ((i * 340 + row * 160 + nowMs / (14 + row * 6)) % (W + 360)) - 180
      ctx.beginPath()
      ctx.ellipse(x, y, 150 + (i % 3) * 40, 16 + row * 6, 0, 0, Math.PI * 2)
      ctx.fill()
    }
  }
}

function drawRays(ctx: CanvasRenderingContext2D, camXWorld: number, camYWorld: number, nowMs: number): void {
  const fade = surfaceFade(camYWorld)
  if (fade <= 0) return
  const soilY = Math.min(H, (UNDERGROUND_TOP - camYWorld) * MINE_ZOOM)
  if (soilY <= 0) return
  ctx.save()
  ctx.globalCompositeOperation = 'lighter'
  for (let i = 0; i < 3; i++) {
    const x0 = ((i * 470 + 120 - camXWorld * MINE_ZOOM * 0.1) % (W + 320)) - 160
    const wTop = 70 + i * 22
    const a = 0.05 * (0.7 + 0.3 * Math.sin(nowMs / 3200 + i * 1.9)) * fade
    const grad = ctx.createLinearGradient(0, 0, 0, soilY)
    grad.addColorStop(0, hexA('#ffd23f', a))
    grad.addColorStop(1, hexA('#ffd23f', 0))
    ctx.fillStyle = grad
    ctx.beginPath()
    ctx.moveTo(x0, 0)
    ctx.lineTo(x0 + wTop, 0)
    ctx.lineTo(x0 + wTop + 150, soilY)
    ctx.lineTo(x0 + 150, soilY)
    ctx.closePath()
    ctx.fill()
  }
  ctx.restore()
}

/** Braises du FOND (monde-space) : étincelles de lave qui montent en boucle autour du sol É3 —
 *  visibles seulement quand la caméra est en bas (culling naturel par la caméra). */
function drawEmbers(ctx: CanvasRenderingContext2D, nowMs: number): void {
  ctx.save()
  ctx.globalCompositeOperation = 'lighter'
  for (let i = 0; i < 14; i++) {
    const x = 50 + ((i * 173 + 40) % 1240) + 6 * Math.sin(nowMs / 900 + i)
    const y = 940 - ((nowMs / 18 + i * 67) % 170)
    const a = (0.2 + 0.3 * (0.5 + 0.5 * Math.sin(nowMs / 260 + i * 1.7))) * (1 + 0.5 * dramaK * Math.sin(nowMs / 85))
    ctx.globalAlpha = Math.min(1, a) * Math.min(1, (940 - y) / 40) // naissent en douceur en bas
    ctx.fillStyle = i % 3 === 0 ? '#ffd23f' : '#ff5f2a'
    ctx.fillRect(x, y, 1.8, 1.8)
  }
  ctx.restore()
  ctx.globalAlpha = 1
}

/** Bandeau d'ARRIVÉE D'ÉTAGE (écran-space), fondu entrée/sortie sur `k` (0..1 de vie du bandeau).
 *  Il annonce désormais CE QU'ON Y TROUVE (26/07) : le titre, puis la promesse de rareté lue dans
 *  shared/economy — la même table que le serveur applique, donc le bandeau ne peut plus mentir.
 *  Avant, il promettait « gains ×3 » pour un bonus qui coûtait exactement ce qu'il donnait. */
export function renderDepthBanner(ctx: CanvasRenderingContext2D, depth: number, k: number): void {
  const alpha = k < 0.15 ? k / 0.15 : k > 0.75 ? Math.max(0, (1 - k) / 0.25) : 1
  const teinte = depth >= 3 ? '#ff9a3d' : depth === 2 ? '#7cd7ff' : '#ffd23f'
  ctx.save()
  ctx.globalAlpha = alpha
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.shadowColor = 'rgba(0,0,0,0.7)'
  ctx.shadowBlur = 12
  ctx.shadowOffsetY = 3
  ctx.font = 'bold 34px ui-monospace, Menlo, monospace'
  ctx.fillStyle = teinte
  ctx.fillText(depthLabel(depth), W / 2, 150)
  if (depth > 1) { // la surface n'a rien à promettre : c'est le tirage d'origine
    ctx.font = 'bold 19px ui-monospace, Menlo, monospace'
    ctx.fillStyle = '#f0e9d2'
    ctx.fillText(`ici, ${depthPromise(depth)}`, W / 2, 184)
    ctx.font = '15px ui-monospace, Menlo, monospace'
    ctx.fillStyle = '#9a93c0'
    ctx.fillText(`pièces et éclats ×${DEPTH_MULT[depth - 1] ?? 1}`, W / 2, 210)
  }
  ctx.restore()
  ctx.globalAlpha = 1
}

let vignette: HTMLCanvasElement | null = null
function ensureVignette(): void {
  if (vignette) return
  const v = mkLayer(W)
  const g = v.g
  const grad = g.createRadialGradient(W / 2, H * 0.46, H * 0.32, W / 2, H * 0.46, H * 0.78)
  grad.addColorStop(0, 'rgba(4,2,10,0)')
  grad.addColorStop(1, 'rgba(4,2,10,0.34)')
  g.fillStyle = grad
  g.fillRect(0, 0, W, H)
  vignette = v.cv
}

// --- HUD « mine » (Lot 4) : bande basse bois + or, médaillons de compteurs --------------------
const HUD_Y = 768
function medallion(ctx: CanvasRenderingContext2D, x: number, emoji: string, value: string): number {
  const cy = HUD_Y + 24
  ctx.beginPath()
  ctx.arc(x + 16, cy, 15, 0, Math.PI * 2)
  ctx.fillStyle = '#161230'
  ctx.fill()
  ctx.strokeStyle = hexA('#ffd23f', 0.6)
  ctx.lineWidth = 2
  ctx.stroke()
  ctx.font = '16px ui-monospace, Menlo, monospace'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillStyle = '#f0e9d2'
  ctx.fillText(emoji, x + 16, cy + 1)
  ctx.font = 'bold 20px ui-monospace, Menlo, monospace'
  ctx.textAlign = 'left'
  ctx.fillStyle = '#f0e9d2'
  ctx.fillText(value, x + 38, cy + 1)
  return x + 38 + ctx.measureText(value).width + 22 // x du prochain médaillon
}

// pity : jauge de chance (Mine fun, lot A) — compteurs HONNÊTES « avant garanti » dérivés des
// garanties dures du serveur (PITY_EPIC=10 / PITY_MEGA=40 de shared/loot-draw). Pulsation quand
// le garanti approche : l'anticipation se voit.
export function renderMineHud(
  ctx: CanvasRenderingContext2D,
  wallet: { bricks: number; coins: number; shards: number } | null,
  titleLbl: string | null,
  touch: boolean,
  pity: { epic: number; mega: number } | null = null,
  nowMs = 0,
): void {
  const wood = ctx.createLinearGradient(0, HUD_Y, 0, H)
  wood.addColorStop(0, '#3a2a18')
  wood.addColorStop(1, '#241708')
  ctx.fillStyle = wood
  ctx.fillRect(0, HUD_Y, W, H - HUD_Y)
  ctx.fillStyle = hexA('#1a1008', 0.5) // rainures du bois
  for (let x = 90; x < W; x += 260) ctx.fillRect(x, HUD_Y + 4, 3, H - HUD_Y - 8)
  const gild = ctx.createLinearGradient(0, HUD_Y, 0, HUD_Y + 3) // liseré or
  gild.addColorStop(0, '#ffd23f')
  gild.addColorStop(1, '#e0940f')
  ctx.fillStyle = gild
  ctx.fillRect(0, HUD_Y, W, 3)
  if (wallet) {
    let x = 16
    x = medallion(ctx, x, '🧱', String(wallet.bricks))
    x = medallion(ctx, x, '🪙', String(wallet.coins))
    x = medallion(ctx, x, '💎', String(wallet.shards))
    if (pity) {
      const cy = HUD_Y + 24
      const epicLeft = Math.max(1, 10 - pity.epic) // casses restantes avant épique garanti
      const megaLeft = Math.max(1, 40 - pity.mega) // ...avant méga garanti
      ctx.beginPath()
      ctx.arc(x + 16, cy, 15, 0, Math.PI * 2)
      ctx.fillStyle = '#161230'
      ctx.fill()
      ctx.strokeStyle = 'rgba(185,139,255,0.35)' // piste de l'anneau
      ctx.lineWidth = 3
      ctx.stroke()
      ctx.beginPath() // anneau de progression vers l'épique garanti
      ctx.arc(x + 16, cy, 15, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * Math.min(1, pity.epic / 10))
      ctx.strokeStyle = '#b98bff'
      ctx.stroke()
      ctx.font = '15px ui-monospace, Menlo, monospace'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText('🍀', x + 16, cy + 1)
      const pulseE = epicLeft <= 2 ? 0.55 + 0.45 * Math.sin(nowMs / 160) : 1
      const pulseM = megaLeft <= 5 ? 0.55 + 0.45 * Math.sin(nowMs / 130) : 1
      ctx.textAlign = 'left'
      ctx.font = 'bold 14px ui-monospace, Menlo, monospace'
      ctx.globalAlpha = Math.max(0.35, pulseE)
      ctx.fillStyle = '#b98bff'
      ctx.fillText(epicLeft <= 2 ? `✨ épique dans ${epicLeft} !` : `✨ ${epicLeft}`, x + 38, cy - 8)
      ctx.globalAlpha = Math.max(0.35, pulseM)
      ctx.fillStyle = '#ffd23f'
      ctx.fillText(megaLeft <= 5 ? `🌟 MÉGA dans ${megaLeft} !` : `🌟 ${megaLeft}`, x + 38, cy + 10)
      ctx.globalAlpha = 1
    }
  } else {
    ctx.font = 'bold 22px ui-monospace, Menlo, monospace'
    ctx.textAlign = 'left'
    ctx.textBaseline = 'middle'
    ctx.fillStyle = '#f0e9d2'
    ctx.fillText('La Mine aux briques', 18, HUD_Y + 25)
  }
  ctx.textBaseline = 'middle'
  if (titleLbl) {
    ctx.textAlign = 'center'
    ctx.font = 'bold 18px ui-monospace, Menlo, monospace'
    ctx.fillStyle = '#ffd23f'
    ctx.fillText(`👑 ${titleLbl}`, W / 2, HUD_Y + 25)
  }
  ctx.textAlign = 'right'
  ctx.font = '15px ui-monospace, Menlo, monospace'
  ctx.fillStyle = '#cbc4e0'
  ctx.fillText(touch ? 'Creuse le sol · Boutique · Retour' : 'Flèches + A/S : creuse le sol · Échap : retour', W - 16, HUD_Y + 25)
}

// --- LA PLUIE D'OR (Mine fun, lot C) : pièces jouables en espace MONDE ------------------------
// Pièces dorées qui « tournent » (largeur d'ellipse qui oscille), reflet, et une étincelle qui
// passe de pièce en pièce (l'or appelle le coureur). Les prises ne se dessinent plus.
function drawRainCoins(ctx: CanvasRenderingContext2D, coins: readonly RainCoin[], nowMs: number): void {
  for (let i = 0; i < coins.length; i++) {
    const c = coins[i]!
    if (c.taken) continue
    const spin = 0.35 + 0.65 * Math.abs(Math.sin(nowMs / 160 + i * 1.1))
    ctx.save()
    ctx.translate(c.x, c.y)
    ctx.fillStyle = '#e0940f'
    ctx.beginPath()
    ctx.ellipse(0, 0, 6 * spin, 6, 0, 0, Math.PI * 2)
    ctx.fill()
    ctx.fillStyle = '#ffd23f'
    ctx.beginPath()
    ctx.ellipse(-0.8, -0.8, 4.6 * spin, 4.6, 0, 0, Math.PI * 2)
    ctx.fill()
    ctx.fillStyle = '#fff3b0'
    ctx.fillRect(-1.4 * spin, -3, Math.max(0.8, 2 * spin), 2)
    ctx.restore()
    if ((i + (nowMs / 650 | 0)) % 5 === 0) { // l'étincelle vagabonde
      ctx.fillStyle = 'rgba(255,255,255,0.85)'
      ctx.fillRect(c.x + 4, c.y - 7, 1.6, 1.6)
    }
  }
}

// --- LE SUSPENS DE CREUSE (Lot 3) : fissures + lueur par paliers, en écran-space --------------
// Dessiné PAR-DESSUS la scène par le contrôleur pendant la phase 'suspense'. (cx, cy) = point
// d'impact de la pioche à l'écran (dessus-centre de la case). Le tracé des fissures est
// DÉTERMINISTE par case (PRNG seedé) : elles se propagent par stades sans jamais « sauter ».
// La lumière semble SORTIR du sol : fissures sombres larges d'abord (la pierre se brise), le même
// tracé recouvert plus fin dans la couleur du palier (la lueur dans la fente), halo additif enfin.
// v2 (21/07 nuit) : la creuse est une MISE EN SCÈNE — coups de pioche (flash + onde de choc),
// fissures en propagation CONTINUE (segment de tête qui pousse), étincelles qui saignent des
// fentes, ONDE colorée à chaque palier de rareté, embrasement arc-en-ciel au palier MÉGA, et
// IMPLOSION (tout converge vers un point blanc) sur les 400 dernières ms. Budget par frame borné
// (< ~60 primitives, zéro shadowBlur) — règle perf de la spec Mine.
// v3 (fix DÉCALAGE, 21/07 soir) : tous les effets ANCRÉS sont dessinés dans un repère attaché au
// TROU et à la CAMÉRA DU FRAME (translate au trou-écran + scale zoom/3) — l'ancrage suit le punch
// pixel pour pixel, un désalignement est impossible par construction. Les voiles plein écran
// (assombrissement, teinte de palier) restent en écran-space. + DÉBRIS EN LÉVITATION : des cailloux
// aspirés au-dessus de la case pendant la montée en tension (le sol perd sa gravité).
export function renderSuspense(ctx: CanvasRenderingContext2D, clock: SuspenseClock, holeWx: number, holeWy: number, nowMs: number, cell: number, cam: { x: number; y: number; zoom: number }): void {
  const t = clock.t
  const glow = suspenseGlow(clock)
  const imp = implosionP(clock)
  const pulse = 0.85 + 0.15 * Math.sin(nowMs / 120)
  // assombrissement : suit la lueur, se resserre encore à l'implosion (écran-space)
  ctx.fillStyle = `rgba(6,4,14,${Math.min(0.62, glow.intensity * 0.5 + imp * 0.25)})`
  ctx.fillRect(0, 0, W, H)
  // repère ANCRÉ : (0,0) = dessus-centre de la case, unités « écran zoom 3 » (les réglages historiques)
  const hs = { x: (holeWx - cam.x) * cam.zoom, y: (holeWy - cam.y) * cam.zoom }
  ctx.save()
  ctx.translate(hs.x, hs.y)
  ctx.scale(cam.zoom / 3, cam.zoom / 3)
  const cx = 0, cy = 0

  // 1) COUPS DE PIOCHE : étoile d'impact + onde de choc circulaire (220 ms chacun)
  const hit = pickHitFlash(t)
  if (hit) {
    const p = hit.p
    const fl = Math.max(0, 1 - p * 2.4) // l'étoile s'éteint vite, l'onde survit
    ctx.save()
    if (fl > 0) {
      ctx.strokeStyle = `rgba(255,250,235,${(fl * 0.9).toFixed(3)})`
      ctx.lineWidth = 2
      ctx.lineCap = 'round'
      const len = 10 + (1 - fl) * 8
      for (let k = 0; k < 4; k++) {
        const a = k * Math.PI / 2 + Math.PI / 4
        ctx.beginPath()
        ctx.moveTo(cx + Math.cos(a) * 3, cy + Math.sin(a) * 3)
        ctx.lineTo(cx + Math.cos(a) * len, cy + Math.sin(a) * len)
        ctx.stroke()
      }
    }
    ctx.strokeStyle = `rgba(255,255,255,${((1 - p) * 0.45).toFixed(3)})`
    ctx.lineWidth = 0.5 + 2.5 * (1 - p)
    ctx.beginPath(); ctx.arc(cx, cy, 6 + p * 46, 0, Math.PI * 2); ctx.stroke()
    ctx.restore()
  }

  // 2) FISSURES CONTINUES : 9 branches x 2 segments, le segment de tête POUSSE (fraction) ;
  //    2 passes (pierre sombre large, puis la lueur fine dans la fente) sur le MÊME tirage.
  const segF = crackSegments(t)
  const blaze = megaBlaze(clock)
  const rainbow = rarityRays('mega')
  if (segF > 0) {
    const cl01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v)
    for (let pass = 0; pass <= 1; pass++) {
      ctx.lineWidth = pass === 0 ? 5 : 2
      ctx.lineCap = 'round'
      const r2 = mulberry32(SEED + cell * 31) // même tirage aux deux passes : tracés identiques
      for (let b = 0; b < 9; b++) {
        // éventail INFÉRIEUR seulement (le sol se fissure vers le bas/les côtés, jamais dans l'air)
        const a = Math.PI * (-0.08 + 1.16 * (b / 8)) + (r2() - 0.5) * 0.22
        const l1f = 26 + r2() * 30
        const bend = (r2() - 0.5) * 1.1
        const l2f = 18 + r2() * 26
        const v1 = cl01(segF - b)           // segment 1 de la branche b : indice b
        const v2 = cl01(segF - 9 - b)       // segment 2 (coude) : indice 9+b
        if (v1 <= 0) continue               // (le tirage r2 est déjà consommé : déterminisme intact)
        ctx.strokeStyle = pass === 0 ? '#0a0714'
          : blaze > 0 ? hexA(rainbow[(b + Math.floor(nowMs / 90)) % rainbow.length]!, Math.min(1, 0.55 + blaze * 0.45))
            : hexA(glow.color, Math.min(1, glow.intensity * pulse))
        const x1 = cx + Math.cos(a) * l1f * v1, y1 = cy + Math.sin(a) * l1f * v1
        ctx.beginPath()
        ctx.moveTo(cx, cy)
        ctx.lineTo(x1, y1)
        if (v2 > 0) ctx.lineTo(x1 + Math.cos(a + bend) * l2f * v2, y1 + Math.sin(a + bend) * l2f * v2)
        ctx.stroke()
      }
    }
    // la lumière SAIGNE des fentes : étincelles aspirées vers le haut (plafond 10, déterministes)
    ctx.save()
    ctx.globalCompositeOperation = 'lighter'
    for (let k = 0; k < 10; k++) {
      if (k >= segF) break
      const ph = (nowMs / 950 + k * 0.107) % 1
      const ak = Math.PI * (0.06 + 0.88 * ((k * 0.37) % 1))
      const dk = 10 + ((k * 53) % 34)
      const sx = cx + Math.cos(ak) * dk
      const sy = cy + Math.sin(ak) * dk * 0.7 - ph * 30
      ctx.globalAlpha = (1 - ph) * Math.min(1, glow.intensity) * 0.85
      ctx.fillStyle = blaze > 0 ? rainbow[k % rainbow.length]! : glow.color
      ctx.fillRect(sx, sy, 1.7, 1.7)
    }
    ctx.restore()
  }

  // 3) ONDE DE PALIER : anneau coloré qui traverse le sol + bref voile teinté au franchissement
  const wave = palierWave(clock)
  if (wave && clock.rarity !== null) {
    const col = SUSPENSE_PALIERS[clock.rarity][wave.palier]!.color
    ctx.save()
    ctx.globalCompositeOperation = 'lighter'
    if (wave.p < 0.12) { // voile plein écran : hors du repère ancré (coordonnées canvas brutes)
      ctx.save(); ctx.setTransform(1, 0, 0, 1, 0, 0)
      ctx.fillStyle = hexA(col, 0.1 * (1 - wave.p / 0.12)); ctx.fillRect(0, 0, W, H)
      ctx.restore()
    }
    ctx.strokeStyle = hexA(col, (1 - wave.p) * 0.5)
    ctx.lineWidth = 1 + 3 * (1 - wave.p)
    ctx.beginPath(); ctx.arc(cx, cy, 24 + wave.p * 250, 0, Math.PI * 2); ctx.stroke()
    ctx.restore()
  }

  // 4) HALO : la lumière qui filtre — s'ÉTRANGLE à l'implosion (le point blanc avant le boom)
  ctx.save()
  ctx.globalCompositeOperation = 'lighter'
  const hr = 150 * (1 - imp * 0.55)
  const hi = Math.min(1, glow.intensity * pulse) * (0.55 + imp * 0.35)
  const halo = ctx.createRadialGradient(cx, cy, 0, cx, cy, hr)
  halo.addColorStop(0, hexA(glow.color, hi))
  halo.addColorStop(0.55, hexA(glow.color, hi * 0.33))
  halo.addColorStop(1, hexA(glow.color, 0))
  ctx.fillStyle = halo
  ctx.fillRect(cx - hr, cy - hr, hr * 2, hr * 2)
  ctx.restore()

  // 5) IMPLOSION : la poussière CONVERGE vers un cœur blanc aveuglant
  if (imp > 0) {
    ctx.save()
    ctx.globalCompositeOperation = 'lighter'
    for (let i = 0; i < 10; i++) {
      const ang = i * 0.628 + nowMs / 700
      const ph = 1 - ((nowMs / 240 + i * 0.1) % 1) // 1 -> 0 : chaque grain FONCE vers le centre
      const d = ph * 120 * (1 - imp * 0.3)
      ctx.globalAlpha = imp * (1 - ph) * 0.9
      ctx.fillStyle = i % 3 === 0 ? '#ffffff' : glow.color
      ctx.fillRect(cx + Math.cos(ang) * d, cy + Math.sin(ang) * d * 0.6, 2, 2)
    }
    const core = ctx.createRadialGradient(cx, cy, 0, cx, cy, 4 + imp * 10)
    core.addColorStop(0, `rgba(255,255,255,${imp.toFixed(3)})`)
    core.addColorStop(1, 'rgba(255,255,255,0)')
    ctx.fillStyle = core
    ctx.globalAlpha = 1
    ctx.beginPath(); ctx.arc(cx, cy, 4 + imp * 10, 0, Math.PI * 2); ctx.fill()
    ctx.restore()
  }

  // DÉBRIS EN LÉVITATION : cailloux aspirés au-dessus de la case (déterministes, plafond 8),
  // d'autant plus nombreux/hauts que la lueur monte — coupés pendant l'implosion (tout converge).
  const lev = Math.min(1, glow.intensity) * (1 - imp)
  if (lev > 0.12 && crackSegments(t) > 0) {
    ctx.save()
    for (let i = 0; i < 8; i++) {
      if (i / 8 > lev) break
      const ph = (nowMs / 1400 + i * 0.125) % 1
      const lx = cx - 34 + ((i * 61) % 68) + Math.sin(nowMs / 460 + i * 2.2) * 4
      const ly = cy + 4 - ph * (26 + (i % 3) * 8)
      ctx.globalAlpha = Math.min(1, (1 - ph) * 1.4) * lev * 0.9
      ctx.fillStyle = i % 3 === 0 ? '#6b5a3a' : '#3a2a1c'
      const sz2 = 2 + (i % 3)
      ctx.save()
      ctx.translate(lx, ly)
      ctx.rotate(nowMs / 800 + i)
      ctx.fillRect(-sz2 / 2, -sz2 / 2, sz2, sz2)
      ctx.restore()
    }
    ctx.restore()
  }
  ctx.restore() // fin du repère ancré trou+caméra
}

// --- LES COSMÉTIQUES ÉQUIPÉS, DANS LA MINE (26/07) --------------------------------------------
// Le défaut de fond corrigé ce jour-là : la Mine a son propre rendu, et ce rendu ignorait tout de
// l'équipement du joueur. On gagnait un familier à la Mine, on l'équipait à la Mine, et il
// n'apparaissait nulle part dans la Mine ; les « gravats de creusage » — le seul cosmétique
// littéralement thématisé sur le fait de creuser — ne sortaient jamais des trous qu'on y creuse.
// Les deux modules du jeu (particules, familier) travaillent en espace MONDE et n'ont besoin
// d'aucune adaptation : il suffisait de les appeler. 100 % cosmétique, hors simulation.
function drawMineCosmetics(
  ctx: CanvasRenderingContext2D, prev: GameState | null, cur: GameState, alpha: number,
  sheet: HTMLImageElement | null,
): void {
  // Gravats de creusage + traînée de dash teintés par les loots équipés (particles lit loadout).
  drawParticles(ctx, prev, cur, cur.tick + alpha, 0)
  const r = cur.runners[0]
  const petId = equipped('pet')
  if (!sheet || !r || !petDessinable(petId)) return
  if (prev === null) resetPetHistory() // nouvelle scène : le compagnon ne traîne pas une vieille trace
  const p = prev?.runners[0]
  const px = lerp(p && p.alive && r.alive ? p.x : r.x, r.x, alpha) * PXW
  const py = lerp(p && p.alive && r.alive ? p.y : r.y, r.y, alpha) * PXW
  updateAndDrawPet({ ctx, sheet }, 0, petId, px, py, r.face, cur.tick, alpha, {
    alive: r.alive,
    events: cur.events,   // il gratte quand tu creuses : sa meilleure réaction est ici chez elle
    goldDx: null,         // la Mine n'a pas d'or au sol (la pluie d'or est un spectacle, pas des cases)
    monkDx: null,         // ni moine : aucune alerte, le compagnon reste serein
    runnerMoving: p ? p.x !== r.x || p.y !== r.y : false,
    cellNear: (cell) => Math.abs((cell % cur.cols) * TSW - px) + Math.abs(Math.floor(cell / cur.cols) * TSW - py) < 1.3 * TSW,
    solidAt: (wx, wy) => { // l'araignée s'accroche au plafond quand il y en a un au-dessus d'elle
      const c = Math.floor(wx / TSW), rw = Math.floor(wy / TSW)
      return c >= 0 && rw >= 0 && c < cur.cols && rw < cur.rows && isSolid(cur.terrain[rw * cur.cols + c] ?? T.Empty)
    },
  })
}

// --- assemblage -------------------------------------------------------------------------------
// hint : contexte de l'invite « creuse » (null = pas d'invite, p.ex. scène gelée sous la révélation).
// rain : pièces de la pluie d'or en cours (null = pas de pluie) — dessinées DEVANT le héros.
// sheet : feuille de sprites (familier). Absente = pas de compagnon dessiné, le reste est intact.
export function renderMine(
  ctx: CanvasRenderingContext2D, prev: GameState | null, cur: GameState, alpha: number,
  cam: MineCam, digUntil: number[], nowMs: number,
  hint: { hasBricks: boolean; touch: boolean; cost?: number; depth?: number } | null,
  rain: readonly RainCoin[] | null = null,
  sheet: HTMLImageElement | null = null,
): void {
  ensureLayers()
  ensureSoilBand()
  ensureVignette()
  drawParallax(ctx, cam.x, cam.y, nowMs) // écran-space : couches ancrées monde (la caverne du haut)
  drawRays(ctx, cam.x, cam.y, nowMs)     // rayons obliques de surface : entre le fond et le sol
  drawSoilBand(ctx, cam.x, cam.y, nowMs) // le sous-sol unifié (3 étages), fenêtré caméra X et Y
  ctx.save()
  ctx.scale(cam.zoom, cam.zoom)
  ctx.translate(-cam.x, -cam.y) // ...puis tout en espace MONDE (48 px/case)
  drawSoil(ctx, cur)
  drawDust(ctx, nowMs)      // poussières dorées en suspension (défilent avec la caméra)
  drawFireflies(ctx, nowMs) // lucioles vertes en Lissajous lent
  drawEmbers(ctx, nowMs)    // braises de lave du fond (visibles à l'étage 3)
  drawHero(ctx, prev, cur, alpha, digUntil, nowMs)
  drawMineCosmetics(ctx, prev, cur, alpha, sheet) // familier + gravats/traînées teintés par l'équipement
  if (rain) drawRainCoins(ctx, rain, nowMs) // la pluie d'or, devant le héros (on la voit s'attraper)
  ctx.restore()
  drawFog(ctx, cam.y, nowMs)          // nappes de brume de SURFACE (s'estompent en profondeur)
  ctx.drawImage(vignette!, 0, 0)      // vignette bakée : le regard converge
  // L'invite de creusage s'efface PENDANT une emote : elle se dessine en espace écran, donc par
  // dessus la bulle, et venait barrer l'emoji. Quand on fait le clown, on ne creuse pas.
  if (hint && !currentEmote(nowMs)) drawHint(ctx, cur, cam, nowMs, hint.hasBricks, hint.touch, hint.cost ?? 1, hint.depth ?? 1)
}
